import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha, styled } from '@mui/material/styles';
import type { CheckboxVisualVariant } from './Checkbox.props';

interface CheckboxStyleProps {
  visualVariant: CheckboxVisualVariant;
}

const SELECTION_DISABLED_SELECTED_ICON_SELECTOR = [
  '&.Mui-disabled.Mui-checked [data-checkbox-selection-icon]',
  '&.Mui-disabled.MuiCheckbox-indeterminate [data-checkbox-selection-icon]',
].join(', ');

const shouldForwardCheckboxProp = (prop: PropertyKey) => prop !== 'visualVariant';

export const StyledCheckboxRoot = styled(MuiCheckbox, {
  shouldForwardProp: shouldForwardCheckboxProp,
})<CheckboxStyleProps>(({ theme, visualVariant }) => (visualVariant === 'selection' ? {
  alignItems: 'center',
  backgroundColor: 'transparent',
  borderRadius: 0,
  boxSizing: 'border-box',
  color: theme.palette.neutral[500],
  display: 'inline-flex',
  flex: '0 0 var(--checkbox-selection-size, 16px)',
  height: 'var(--checkbox-selection-size, 16px)',
  justifyContent: 'center',
  margin: 0,
  maxHeight: 'var(--checkbox-selection-size, 16px)',
  maxWidth: 'var(--checkbox-selection-size, 16px)',
  minHeight: 'var(--checkbox-selection-size, 16px)',
  minWidth: 'var(--checkbox-selection-size, 16px)',
  padding: 0,
  width: 'var(--checkbox-selection-size, 16px)',
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: 'transparent',
  },
  '&.Mui-disabled': {
    color: alpha(theme.palette.neutral[500], 0.36),
  },
  '&.Mui-disabled [data-checkbox-selection-icon]': {
    backgroundColor: theme.palette.neutral[100],
    borderColor: theme.palette.neutral[300],
  },
  [SELECTION_DISABLED_SELECTED_ICON_SELECTOR]: {
    backgroundColor: alpha(theme.palette.brand[500], 0.36),
    borderColor: 'transparent',
  },
} : {}));

export const CheckboxSelectionIcon = styled('span')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.neutral.white,
  border: 'var(--checkbox-selection-border-width, 1px) solid currentColor',
  borderRadius: 'var(--checkbox-selection-radius, 4px)',
  boxSizing: 'border-box',
  color: 'inherit',
  display: 'inline-flex',
  flexShrink: 0,
  height: 'var(--checkbox-selection-size, 16px)',
  justifyContent: 'center',
  position: 'relative',
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  width: 'var(--checkbox-selection-size, 16px)',
}));

export const CheckboxSelectionCheckedIcon = styled(CheckboxSelectionIcon)(({ theme }) => ({
  backgroundColor: theme.palette.brand[500],
  borderColor: 'transparent',
  color: theme.palette.neutral.white,
  '&::after': {
    borderBottom: 'var(--checkbox-selection-mark-stroke-width, 2px) solid currentColor',
    borderLeft: 'var(--checkbox-selection-mark-stroke-width, 2px) solid currentColor',
    boxSizing: 'border-box',
    content: '""',
    height: 'var(--checkbox-selection-check-height, 5px)',
    transform: 'rotate(-45deg) translateY(-1px)',
    width: 'var(--checkbox-selection-check-width, 9px)',
  },
}));

export const CheckboxSelectionIndeterminateIcon = styled(CheckboxSelectionCheckedIcon)({
  '&::after': {
    backgroundColor: 'currentColor',
    border: 0,
    borderRadius: 'var(--checkbox-selection-mark-stroke-width, 2px)',
    height: 'var(--checkbox-selection-mark-stroke-width, 2px)',
    transform: 'none',
    width: 'var(--checkbox-selection-indeterminate-width, 8px)',
  },
});

export const StyledCheckboxLabel = styled(FormControlLabel, {
  shouldForwardProp: prop => prop !== 'hasError',
})<{ hasError?: boolean }>(({ hasError = false, theme }) => {
  const stateColor = hasError ? theme.palette.error.main : theme.palette.primary.main;

  return {
    marginLeft: 0,
    marginRight: 0,
    alignItems: 'center',
    columnGap: theme.spacing(0.5),
    cursor: 'pointer',
    minHeight: 32,
    '& .MuiCheckbox-root': {
      borderRadius: theme.shape.borderRadius,
      color: hasError ? stateColor : undefined,
      padding: theme.spacing(0.5),
      transition: theme.transitions.create(['background-color', 'transform', 'color'], {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
    },
    '& .MuiCheckbox-root.Mui-checked': {
      color: hasError ? stateColor : undefined,
    },
    '&:hover .MuiCheckbox-root': {
      backgroundColor: alpha(stateColor, hasError ? 0.1 : 0.06),
    },
    '&:hover .MuiCheckbox-root.Mui-checked': {
      backgroundColor: alpha(stateColor, 0.1),
    },
    '& .MuiCheckbox-root .MuiSvgIcon-root': {
      transition: theme.transitions.create(['transform', 'filter'], {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
    },
    '& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root': {
      filter: `drop-shadow(0 1px 2px ${alpha(stateColor, 0.35)})`,
      transform: 'scale(1.06)',
    },
    '&:active .MuiCheckbox-root .MuiSvgIcon-root': {
      transform: 'scale(0.96)',
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 16,
      lineHeight: 1.35,
    },
  };
});
